/**
 * IT-DRP Manager Dashboard - Command Center
 * Displays: Profile status, Resiliency gaps, Dependencies, Orchestration tools
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import {
  Server,
  Cloud,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Database,
  Shield,
  Activity,
  Bell,
  RefreshCw,
  Send,
  Zap,
  BarChart3,
  Target,
  FileWarning,
  Monitor,
  HardDrive,
  Globe,
  ChevronRight,
  Users,
  Building2,
  AlertCircle,
} from "lucide-react";

export default function ITDRPAdminDashboard() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [processingData, setProcessingData] = useState(null);
  const [resiliencyGaps, setResiliencyGaps] = useState(null);
  const [dependencies, setDependencies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [dashRes, procRes, gapsRes, depsRes] = await Promise.all([
        api.get("/it-drp-admin/dashboard"),
        api.get("/it-drp-admin/profiles/processing"),
        api.get("/it-drp-admin/resiliency-gaps"),
        api.get("/it-drp-admin/dependencies/aggregate")
      ]);
      
      if (dashRes.data?.success) setDashboardData(dashRes.data.data);
      if (procRes.data?.success) setProcessingData(procRes.data.data);
      if (gapsRes.data?.success) setResiliencyGaps(gapsRes.data.data);
      if (depsRes.data?.success) setDependencies(depsRes.data.data);
    } catch (error) {
      console.error("Failed to fetch dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const sendReminders = async () => {
    try {
      await api.post("/it-drp-admin/send-reminders");
      fetchDashboardData();
    } catch (error) {
      console.error("Failed to send reminders:", error);
    }
  };

  const summary = dashboardData?.summary || {};
  const resiliency = dashboardData?.resiliency || {};
  const infraBreakdown = dashboardData?.infrastructure_breakdown || {};
  const drBreakdown = dashboardData?.dr_strategy_breakdown || {};

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex items-center gap-3 text-sky-700">
          <div className="w-6 h-6 border-2 border-sky-700 border-t-transparent rounded-full animate-spin" />
          <span>Loading IT-DRP Command Center...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="it-drp-admin-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-700 to-teal-600 flex items-center justify-center shadow-lg">
              <Monitor className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">IT-DRP Command Center</h1>
              <p className="text-slate-500">Application Profile Tracking & Orchestration</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={fetchDashboardData}>
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button variant="outline" onClick={sendReminders}>
            <Send className="w-4 h-4 mr-2" /> Send Reminders
          </Button>
          <Button className="bg-red-600 hover:bg-red-700">
            <Zap className="w-4 h-4 mr-2" /> Activate IT-DRP
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Total Applications</p>
                <p className="text-3xl font-bold text-blue-700">{summary.total_applications || 0}</p>
              </div>
              <Server className="w-10 h-10 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-green-50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600 font-medium">Profiles Completed</p>
                <p className="text-3xl font-bold text-emerald-700">{summary.profiles_completed || 0}</p>
              </div>
              <CheckCircle2 className="w-10 h-10 text-emerald-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600 font-medium">Pending</p>
                <p className="text-3xl font-bold text-amber-700">{summary.profiles_pending || 0}</p>
              </div>
              <Clock className="w-10 h-10 text-amber-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-rose-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">Critical Gaps</p>
                <p className="text-3xl font-bold text-red-700">{resiliency.critical_gaps || 0}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-red-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">Completion Rate</p>
                <p className="text-3xl font-bold text-purple-700">{summary.completion_rate || 0}%</p>
              </div>
              <Target className="w-10 h-10 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-2xl">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="processing">Processing Status</TabsTrigger>
          <TabsTrigger value="gaps">Resiliency Gaps</TabsTrigger>
          <TabsTrigger value="dependencies">Dependencies</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Infrastructure Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Server className="w-5 h-5 text-sky-700" />
                  Infrastructure Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <HardDrive className="w-4 h-4 text-slate-600" />
                      <span className="text-sm">On-Premises</span>
                    </div>
                    <Badge variant="outline">{infraBreakdown.on_prem || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Cloud className="w-4 h-4 text-blue-600" />
                      <span className="text-sm">Cloud (IaaS/PaaS)</span>
                    </div>
                    <Badge variant="outline">{infraBreakdown.cloud || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-emerald-600" />
                      <span className="text-sm">SaaS</span>
                    </div>
                    <Badge variant="outline">{infraBreakdown.saas || 0}</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Activity className="w-4 h-4 text-purple-600" />
                      <span className="text-sm">Hybrid</span>
                    </div>
                    <Badge variant="outline">{infraBreakdown.hybrid || 0}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* DR Strategy Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-emerald-600" />
                  DR Strategy Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(drBreakdown).map(([strategy, count]) => (
                    <div key={strategy} className="flex items-center justify-between">
                      <span className="text-sm capitalize">{strategy.replace(/_/g, " ")}</span>
                      <Badge variant="secondary">{count}</Badge>
                    </div>
                  ))}
                  {Object.keys(drBreakdown).length === 0 && (
                    <p className="text-sm text-slate-500 text-center py-4">No DR strategies configured yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Resiliency Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                Resiliency Alignment Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4">
                <div className="text-center p-4 bg-emerald-50 rounded-lg">
                  <p className="text-2xl font-bold text-emerald-600">{resiliency.aligned_profiles || 0}</p>
                  <p className="text-sm text-emerald-700">Aligned</p>
                </div>
                <div className="text-center p-4 bg-amber-50 rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">{(resiliency.total_with_gaps || 0) - (resiliency.critical_gaps || 0)}</p>
                  <p className="text-sm text-amber-700">Minor Gaps</p>
                </div>
                <div className="text-center p-4 bg-red-50 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">{resiliency.critical_gaps || 0}</p>
                  <p className="text-sm text-red-700">Critical Gaps</p>
                </div>
                <div className="text-center p-4 bg-slate-100 rounded-lg">
                  <p className="text-2xl font-bold text-slate-600">{resiliency.average_gap_hours || 0}h</p>
                  <p className="text-sm text-slate-700">Avg Gap</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Processing Status Tab */}
        <TabsContent value="processing" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Application Profile Processing Status</CardTitle>
              <CardDescription>Real-time status of all application profiles</CardDescription>
            </CardHeader>
            <CardContent>
              {processingData?.tasks?.length > 0 ? (
                <div className="space-y-3">
                  {processingData.tasks.map((task) => (
                    <div 
                      key={task.id} 
                      className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                        task.status === 'completed' ? 'border-emerald-200 bg-emerald-50' :
                        task.status === 'overdue' ? 'border-red-200 bg-red-50' :
                        task.status === 'in_progress' ? 'border-blue-200 bg-blue-50' :
                        'border-slate-200 bg-slate-50 hover:border-sky-300'
                      }`}
                      data-testid={`processing-task-${task.id}`}
                      onClick={() => task.status !== 'completed' && navigate(`/application-profile/${task.id}`)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className={`w-3 h-3 rounded-full ${
                            task.status === 'completed' ? 'bg-emerald-500' :
                            task.status === 'overdue' ? 'bg-red-500 animate-pulse' :
                            task.status === 'in_progress' ? 'bg-blue-500' :
                            'bg-slate-400'
                          }`} />
                          <div>
                            <p className="font-medium text-slate-900">{task.application_name}</p>
                            <p className="text-sm text-slate-500">{task.business_unit}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge 
                            variant={
                              task.status === 'completed' ? 'default' :
                              task.status === 'overdue' ? 'destructive' :
                              'secondary'
                            }
                            className={task.status === 'completed' ? 'bg-emerald-500' : ''}
                          >
                            {task.status.replace('_', ' ')}
                          </Badge>
                          {task.has_critical_gap && (
                            <Badge variant="destructive">
                              <AlertTriangle className="w-3 h-3 mr-1" /> Gap
                            </Badge>
                          )}
                          {task.status !== 'completed' && (
                            <Button size="sm" variant="outline" className="ml-2">
                              <ChevronRight className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500">
                          RTO: {task.required_rto}h | RPO: {task.required_rpo}h
                        </span>
                        {task.status !== 'completed' && (
                          <Progress value={task.completion_percentage || 0} className="w-32 h-2" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Server className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No application profiles in processing queue</p>
                  <p className="text-sm mt-2">Profiles will appear here when BIAs are approved</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Resiliency Gaps Tab */}
        <TabsContent value="gaps" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileWarning className="w-5 h-5 text-red-600" />
                Resiliency Gap Analysis
              </CardTitle>
              <CardDescription>Applications where Strategy RTO exceeds Required RTO</CardDescription>
            </CardHeader>
            <CardContent>
              {resiliencyGaps?.all_gaps?.length > 0 ? (
                <div className="space-y-3">
                  {resiliencyGaps.all_gaps.map((profile) => (
                    <div 
                      key={profile.id}
                      className={`p-4 border rounded-lg ${
                        profile.resiliency_gap?.severity === 'critical' ? 'border-red-300 bg-red-50' :
                        profile.resiliency_gap?.severity === 'medium' ? 'border-amber-300 bg-amber-50' :
                        'border-yellow-200 bg-yellow-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-slate-900">
                            {profile.application_info?.application_name || 'Unknown Application'}
                          </p>
                          <p className="text-sm text-slate-500">
                            {profile.inherited_business_unit}
                          </p>
                        </div>
                        <Badge 
                          variant={profile.resiliency_gap?.severity === 'critical' ? 'destructive' : 'secondary'}
                        >
                          {profile.resiliency_gap?.gap_hours}h Gap
                        </Badge>
                      </div>
                      <div className="mt-3 flex items-center gap-6 text-sm">
                        <div>
                          <span className="text-slate-500">Required RTO:</span>
                          <span className="font-medium ml-1">{profile.inherited_rto}h</span>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                        <div>
                          <span className="text-slate-500">Strategy RTO:</span>
                          <span className="font-medium ml-1 text-red-600">{profile.strategy_rto}h</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-emerald-400" />
                  <p className="font-medium text-emerald-600">All Applications Aligned!</p>
                  <p className="text-sm mt-2">No resiliency gaps detected</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dependencies Tab */}
        <TabsContent value="dependencies" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Database className="w-5 h-5 text-sky-700" />
                Aggregate Technical Dependencies
              </CardTitle>
              <CardDescription>Organization-wide technology stack analysis</CardDescription>
            </CardHeader>
            <CardContent>
              {dependencies?.dependencies ? (
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Databases */}
                  <div>
                    <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                      <Database className="w-4 h-4" /> Databases
                    </h4>
                    <div className="space-y-2">
                      {Object.entries(dependencies.dependencies.databases || {}).map(([db, data]) => (
                        <div key={db} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <span className="text-sm">{db}</span>
                          <Badge variant="outline">{data.count} apps</Badge>
                        </div>
                      ))}
                      {Object.keys(dependencies.dependencies.databases || {}).length === 0 && (
                        <p className="text-sm text-slate-400 italic">No database data</p>
                      )}
                    </div>
                  </div>

                  {/* Cloud Providers */}
                  <div>
                    <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                      <Cloud className="w-4 h-4" /> Cloud Providers
                    </h4>
                    <div className="space-y-2">
                      {Object.entries(dependencies.dependencies.cloud_providers || {}).map(([provider, data]) => (
                        <div key={provider} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <span className="text-sm">{provider}</span>
                          <Badge variant="outline">{data.count} apps</Badge>
                        </div>
                      ))}
                      {Object.keys(dependencies.dependencies.cloud_providers || {}).length === 0 && (
                        <p className="text-sm text-slate-400 italic">No cloud provider data</p>
                      )}
                    </div>
                  </div>

                  {/* Authentication Methods */}
                  <div>
                    <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                      <Shield className="w-4 h-4" /> Authentication
                    </h4>
                    <div className="space-y-2">
                      {Object.entries(dependencies.dependencies.authentication_methods || {}).map(([auth, data]) => (
                        <div key={auth} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <span className="text-sm">{auth}</span>
                          <Badge variant="outline">{data.count} apps</Badge>
                        </div>
                      ))}
                      {Object.keys(dependencies.dependencies.authentication_methods || {}).length === 0 && (
                        <p className="text-sm text-slate-400 italic">No auth data</p>
                      )}
                    </div>
                  </div>

                  {/* DR Locations */}
                  <div>
                    <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                      <Building2 className="w-4 h-4" /> DR Locations
                    </h4>
                    <div className="space-y-2">
                      {Object.entries(dependencies.dependencies.dr_locations || {}).map(([loc, data]) => (
                        <div key={loc} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                          <span className="text-sm">{loc}</span>
                          <Badge variant="outline">{data.count} apps</Badge>
                        </div>
                      ))}
                      {Object.keys(dependencies.dependencies.dr_locations || {}).length === 0 && (
                        <p className="text-sm text-slate-400 italic">No DR location data</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Activity className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>No dependency data available</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
